/**
 * calcCore.js — shared calculation utilities used by QuickCalc.js and the test suite.
 * Extract these functions here so they can be imported independently from the UI.
 */
import { Pokemon, Move, calculate, Field, Side } from '@smogon/calc';
import { NATURE_TABLE } from '../data/commonSets';
import { getGen } from './showdownHelpers';

export const NATURE_STAT_MAP = {
  hp: null, atk: 'attack', def: 'defense',
  spa: 'specialAttack', spd: 'specialDefense', spe: 'speed',
};

export const STATUS_MAP = {
  'Burned': 'brn', 'Paralyzed': 'par', 'Poisoned': 'psn',
  'Badly Poisoned': 'tox', 'Asleep': 'slp', 'Frozen': 'frz',
};

export const QP_ABILITIES = new Set(['Protosynthesis', 'Quark Drive']);

/** Calculate a single final stat value. */
export function calcStat(statKey, base, ev, iv, level, nature) {
  if (!base) return 0;
  const inner = Math.floor((2 * base + iv + Math.floor(ev / 4)) * level / 100);
  if (statKey === 'hp') return inner + level + 10;
  const natInfo = NATURE_TABLE[nature];
  const smogKey = NATURE_STAT_MAP[statKey];
  let mult = 1.0;
  if (natInfo && smogKey) {
    if (natInfo.boost === smogKey)  mult = 1.1;
    if (natInfo.reduce === smogKey) mult = 0.9;
  }
  return Math.floor((inner + 5) * mult);
}

/** Build a @smogon/calc Pokemon from our internal side config. */
export function buildPokemon(genObj, s) {
  const overrides = { baseStats: { ...s.baseStats } };
  if (s.types && s.types.length) overrides.types = s.types;
  const opts = {
    level: s.level, nature: s.nature,
    evs: { ...s.evs }, ivs: { ...s.ivs },
    overrides,
  };
  if (s.item && s.item !== 'None') opts.item = s.item;
  if (s.ability)       opts.ability     = s.ability;
  if (s.status)        opts.status      = STATUS_MAP[s.status] || s.status || undefined;
  if (s.isDynamaxed)   opts.isDynamaxed = true;
  if (s.isTerastallized && s.teraType) opts.teraType = s.teraType;
  if (s.ability && QP_ABILITIES.has(s.ability)) opts.boostedStat = 'auto';
  if (s.abilityOn) opts.abilityOn = true;
  const nb = Object.fromEntries(Object.entries(s.boosts || {}).filter(([, v]) => v !== 0));
  if (Object.keys(nb).length) opts.boosts = nb;
  try { return new Pokemon(genObj, s.showdownName || 'Bulbasaur', opts); }
  catch { return new Pokemon(genObj, 'Bulbasaur', opts); }
}

/** Build a @smogon/calc Field from our internal field config. */
export function buildField(f) {
  const ruins = key => (f.attackerSide[key] || f.defenderSide[key]) || undefined;
  return new Field({
    weather: f.weather || undefined, terrain: f.terrain || undefined,
    isGravity: f.isGravity || undefined,
    isTabletsOfRuin: ruins('isTabletsOfRuin'),
    isSwordOfRuin:   ruins('isSwordOfRuin'),
    isVesselOfRuin:  ruins('isVesselOfRuin'),
    isBeadsOfRuin:   ruins('isBeadsOfRuin'),
    attackerSide: new Side(f.attackerSide),
    defenderSide: new Side(f.defenderSide),
  });
}

/**
 * Run a single outgoing damage calculation (attacker → defender).
 * Returns { minPct, maxPct, avgPct, minDmg, maxDmg, rolls, defHP, desc } or null on error.
 */
export function runCalc(gen, attackerConfig, defenderConfig, moveName, fieldConfig, moveOpts = {}) {
  try {
    const genObj = getGen(gen);
    const aPokemon = buildPokemon(genObj, attackerConfig);
    const dPokemon = buildPokemon(genObj, defenderConfig);
    const fieldObj = buildField(fieldConfig);
    const moveArgs = {};
    if (moveOpts.isCrit) moveArgs.isCrit = true;
    if (moveOpts.hits > 1) moveArgs.hits = moveOpts.hits;
    const moveObj = new Move(genObj, moveName, moveArgs);
    // Apply Charge effect (doubles next Electric move power) — not a native Side property
    if (fieldConfig.attackerSide?.isCharged && moveObj.type === 'Electric') moveObj.bp *= 2;
    const result = calculate(genObj, aPokemon, dPokemon, moveObj, fieldObj);
    const rolls = result.damage;
    if (!Array.isArray(rolls)) return null;
    const defHP = calcStat('hp', defenderConfig.baseStats.hp, defenderConfig.evs.hp,
      defenderConfig.ivs.hp, defenderConfig.level, defenderConfig.nature);
    const minDmg = Math.min(...rolls);
    const maxDmg = Math.max(...rolls);
    const minPct = (minDmg / defHP) * 100;
    const maxPct = (maxDmg / defHP) * 100;
    return { minDmg, maxDmg, minPct, maxPct, avgPct: (minPct + maxPct) / 2, rolls, defHP,
             desc: result.fullDesc() };
  } catch { return null; }
}

/** Default side config (no field effects active). */
export const DEFAULT_SIDE = {
  isReflect: false, isLightScreen: false, isAuroraVeil: false,
  isHelpingHand: false, isFriendGuard: false, isCharged: false,
  isTailwind: false,
  isTabletsOfRuin: false, isSwordOfRuin: false, isVesselOfRuin: false, isBeadsOfRuin: false,
  isSR: false, spikes: 0, steelsurge: false,
};

/** Build a minimal field config, merging caller overrides. */
export function makeField({ weather = '', terrain = '', isGravity = false,
                            attackerSide = {}, defenderSide = {} } = {}) {
  return {
    weather, terrain, isGravity,
    attackerSide: { ...DEFAULT_SIDE, ...attackerSide },
    defenderSide: { ...DEFAULT_SIDE, ...defenderSide },
  };
}

/** Build a Pokemon config from raw data, merging caller overrides. */
export function makeSide(showdownName, baseStats, types, overrides = {}) {
  return {
    showdownName,
    baseStats,
    types,
    level: 50,
    nature: 'Hardy',
    item: 'None',
    ability: '',
    evs: { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 },
    ivs: { hp: 31, atk: 31, def: 31, spa: 31, spd: 31, spe: 31 },
    boosts: {},
    isDynamaxed: false, isTerastallized: false, abilityOn: false,
    ...overrides,
  };
}
